// Pure helpers (no imports) for Loader Input Projection, its path/filter
// selectors, and Egress Keep Only Synthesizer Keys checks.
//
// Path grammar, shared with the schedules `keys.root`: dot-separated field
// names, each one optionally followed by the "[]" array marker (a trailing "[]"
// is allowed), eg. "windows", "visibilities[].timeslots",
// "mediaObjects[].licenses[].timeslots[]". Selectors ("[field=value]") are NOT
// part of the grammar: a selector changes the cardinality of an array, a
// projection must keep it.
// Segments may contain letters, digits, "_", "$" and "-" (the backend accepts
// any field name).
//
// The projection also has FILTERS (projection.filters): paths whose last key
// has selectors, eg. "titles[type=main]". They are another grammar, see the
// section about the filters below.

export const PATH_REGEX = /^[\w$-]+(\[\])?(\.[\w$-]+(\[\])?)*$/;
export const PATH_ERROR =
  "Use dot-separated field names, each one optionally followed by [] " +
  "(eg. windows, visibilities[].timeslots, mediaObjects[].licenses[].timeslots)";
export const SELECTOR_ERROR =
  "Selectors like [field=value] are not allowed: only the optional [] marker";

const MARKERS = /\[\]/g;

// The path without the "[]" markers, which are optional and have no effect:
// the form used to compare paths
export const normalizePath = (path) =>
  typeof path === "string" ? path.trim().replace(MARKERS, "") : "";

/**
 * Syntax check of a projection path.
 * @param {string} path - The path, as it is going to be saved.
 * @returns {string|undefined} The error message, undefined when the path is valid.
 */
export function validatePath(path) {
  if (typeof path !== "string" || !path) return "Empty path";
  if (/\[[^\]]/.test(path)) return SELECTOR_ERROR;
  if (!PATH_REGEX.test(path)) return PATH_ERROR;
}

/**
 * Parses a list of paths, one per line (as pasted or loaded from a text file).
 * Blank lines are skipped; the lines that are not valid paths are returned
 * apart, so they can be shown and fixed.
 * @param {string} text - The text, one path per line.
 * @param {function(string): string|undefined} [validate] - The syntax check of a line.
 * @param {function(string): string} [prepare] - Applied to each line before the check.
 * @returns {{valid: string[], invalid: {path: string, error: string}[]}}
 */
export function parsePathList(text, validate = validatePath, prepare = (line) => line) {
  const valid = [];
  const invalid = [];
  const seen = new Set();

  String(text ?? "")
    .split(/\r?\n/)
    .forEach((raw) => {
      const path = prepare(raw).trim();
      if (!path) return;

      const error = validate(path);
      if (error) {
        invalid.push({ path, error });
        return;
      }

      const key = normalizePath(path);
      if (!seen.has(key)) {
        seen.add(key);
        valid.push(path);
      }
    });

  return { valid, invalid };
}

/**
 * Adds paths to a list, ignoring the ones already present (compared without the
 * "[]" markers, "a[].b" and "a.b" are the same path). The new paths go first.
 * @param {string[]} existing - The current list.
 * @param {string[]} incoming - The paths to add.
 * @returns {{list: string[], added: number, duplicates: number}}
 */
export function mergePaths(existing = [], incoming = []) {
  const seen = new Set(existing.map(normalizePath));
  const fresh = [];
  let duplicates = 0;

  for (const path of incoming) {
    const key = normalizePath(path);
    if (seen.has(key)) duplicates++;
    else {
      seen.add(key);
      fresh.push(path);
    }
  }

  return { list: [...fresh, ...existing], added: fresh.length, duplicates };
}

// ---------------------------------------------------------------------------
// Filters of the projection (projection.filters)
//
// A filter is a path whose LAST key has one or more selectors and the previous
// keys have none, with the optional "[]" markers: "titles[type=main]",
// "genres[].names[language.code=deu]", "titles[type=main|seo]",
// "titles[][language.code=deu][type=main]". A selector is "[field=value]",
// "[field!=value]", "[field=a|b]" (a|b is an OR), "[a.b=value]" (a dotted
// field inside the element); several selectors on the key are in AND, and so are
// the filters of the list. The grammar is the one of parsePath of ares-pipeline.
//
// A filter is checked on the source element before the projection (its field
// does not need to be in the Include) and removes whole elements, so the flat
// arrays stay aligned; "!=" on an element without the field keeps it. A filter
// on a list that the projection does not keep is an error of the backend.
// ---------------------------------------------------------------------------

export const FILTER_ERROR =
  "A filter is a path whose last key has selectors, eg. titles[type=main], " +
  "genres[].names[language.code=deu], titles[type=main|seo]";
export const FILTER_LAST_ERROR =
  "The last key of a filter must have a selector, eg. titles[type=main]";
export const FILTER_INNER_ERROR =
  "Only the last key of a filter can have selectors: genres[].names[language.code=deu], not genres[a=b].names";
export const FILTERS_WITHOUT_INCLUDE_ERROR =
  "Filters work only with an Include: the Include list is empty, so the projection is not applied";

const KEY_REGEX = /^[\w$-]+$/;
const FIELD_REGEX = /^[\w$-]+(\.[\w$-]+)*$/;

// A selector as parsePath of ares-pipeline reads it: "field=a|b", "field!=a".
// Blanks around the field and the values are not part of them.
function parseSelector(expr) {
  const neq = expr.indexOf("!=");
  const eq = neq >= 0 ? neq : expr.indexOf("=");
  const field = eq > 0 ? expr.slice(0, eq).trim() : "";
  if (!field) return { error: `Invalid selector "[${expr}]"` };
  if (!FIELD_REGEX.test(field)) {
    return { error: `Invalid field "${field}" in the selector "[${expr}]"` };
  }

  return {
    field,
    values: expr
      .slice(eq + (neq >= 0 ? 2 : 1))
      .split("|")
      .map((value) => value.trim()),
    negate: neq >= 0
  };
}

// The segments of a filter, with the same reading of the brackets as parsePath
// of ares-pipeline: the first "]" closes a bracket, "[]" is the array marker
// (only after a key), anything else in the brackets is a selector.
export function parseFilterSegments(path) {
  const segments = [];
  let key = "";
  let selectors = [];

  for (let i = 0; i < path.length; i++) {
    const ch = path[i];

    if (ch === "[") {
      const end = path.indexOf("]", i);
      if (end < 0) return { error: 'Missing "]"' };

      const expr = path.slice(i + 1, end);
      if (expr === "") {
        if (!key) return { error: "The array marker [] must follow a key" };
      } else {
        const selector = parseSelector(expr);
        if (selector.error) return selector;
        selectors.push(selector);
      }
      i = end;
    } else if (ch === ".") {
      segments.push({ key, selectors });
      key = "";
      selectors = [];
    } else key += ch;
  }
  segments.push({ key, selectors });

  return { segments };
}

/**
 * Syntax check of a projection filter.
 * @param {string} filter - The filter, as it is going to be saved.
 * @returns {string|undefined} The error message, undefined when the filter is valid.
 */
export function validateFilter(filter) {
  if (typeof filter !== "string" || !filter) return "Empty filter";

  const { segments, error } = parseFilterSegments(filter);
  if (error) return error;
  if (segments.some(({ key }) => !KEY_REGEX.test(key))) return FILTER_ERROR;

  if (segments.slice(0, -1).some(({ selectors }) => selectors.length > 0)) {
    return FILTER_INNER_ERROR;
  }
  if (segments[segments.length - 1].selectors.length === 0) return FILTER_LAST_ERROR;
}

/**
 * The list a filter acts on: its keys, without selectors and "[]" markers
 * (eg. "genres[].names[language.code=deu]" -> "genres.names").
 * @param {string} filter - A valid filter.
 * @returns {string} Empty when the filter cannot be read.
 */
export function filterList(filter) {
  if (typeof filter !== "string") return "";

  const { segments } = parseFilterSegments(filter);
  return segments ? segments.map(({ key }) => key).join(".") : "";
}

// The cells of a pasted spreadsheet row are one filter: the list and the filter
// columns are joined ("titles[]" + "[type=main]" -> "titles[][type=main]")
const joinCells = (line) =>
  line
    .split("\t")
    .map((cell) => cell.trim())
    .join("");

/**
 * Parses a list of filters, one per line, as pasted or loaded from a text file.
 * The cells of a row copied from a spreadsheet (separated by tabs) are joined.
 * Blank lines are skipped, duplicates dropped (with and without "[]"), the lines
 * that are not valid filters are returned apart.
 * @param {string} text - The text, one filter per line.
 * @returns {{valid: string[], invalid: {path: string, error: string}[]}}
 */
export function parseFilterList(text) {
  return parsePathList(text, validateFilter, joinCells);
}

/**
 * The problems of the filters of a projection (only an active one has them).
 * @param {{active?: boolean, include?: string[], protect?: string[], filters?: string[]}} projection
 * @returns {{invalid: {filter: string, error: string}[], withoutInclude: boolean, notKept: string[]}}
 *   - invalid: the filters that are not valid;
 *   - withoutInclude: there are filters and the Include is empty (the projection
 *     is not applied, the filters would not run);
 *   - notKept: the valid filters on a list that the Include and Protect do not
 *     keep, that is neither a path of their tree (the list is, or is an ancestor
 *     of, a kept path) nor under a kept leaf (a kept path is an ancestor of the list).
 */
export function filterProblems(projection) {
  const problems = { invalid: [], withoutInclude: false, notKept: [] };
  const filters = Array.isArray(projection?.filters) ? projection.filters : [];
  if (!projection?.active || filters.length === 0) return problems;

  const valid = [];
  for (const filter of filters) {
    const error = validateFilter(filter);
    if (error) problems.invalid.push({ filter, error });
    else valid.push(filter);
  }

  const kept = projectionIncludes(projection);
  if (kept.length === 0) {
    problems.withoutInclude = true;
    return problems;
  }

  problems.notKept = valid.filter((filter) => {
    const list = filterList(filter);
    return !kept.some((path) => coversRoot(path, list));
  });

  return problems;
}

/**
 * The filters that remove elements the schedules need: a filter on the root of
 * the windows of an active schedule, or on a list that holds it (eg.
 * "visibilities[]" for the root "visibilities[].timeslots"). It also changes the
 * windows that the schedule sees.
 * @param {string[]} filters - The filters of the projection.
 * @param {Map<string, {scheduleId: string, loader: string, key: string}[]>} requirements - From scheduleRequirements.
 * @returns {{filter: string, scheduleId: string, loader: string, root: string}[]}
 */
export function filterWindowWarnings(filters, requirements) {
  const roots = [];
  requirements?.forEach((entries, root) =>
    entries.forEach(({ key, scheduleId, loader }) => {
      if (key === "root") roots.push({ root, scheduleId, loader });
    })
  );

  const warnings = [];
  for (const filter of filters ?? []) {
    if (validateFilter(filter)) continue;

    const list = filterList(filter);
    for (const { root, scheduleId, loader } of roots) {
      if (root === list || root.startsWith(`${list}.`)) {
        warnings.push({ filter, scheduleId, loader, root });
      }
    }
  }

  return warnings;
}

export const filterWindowMessage = ({ filter, scheduleId, loader, root }) =>
  `The filter ${filter} also changes the windows that the schedule ${scheduleId} of the loader ${loader} sees (Root ${root})`;

// ---------------------------------------------------------------------------
// The keys and values "after the Projection"
//
// At runtime the transformer, the synthesizer and the fingerprint see the
// document AFTER the projection and its filters (projection + filters ->
// transformer -> synthesis -> fingerprint). A rule on a field that the
// projection removes always reads nothing, and a rule on a value that a filter
// removes never finds it. So, with an active projection that has an Include,
// - a key is kept when a kept path (Include or Protect) is the key or an
//   ancestor of it, or when the key is an ancestor of a kept path (an object
//   that is kept in part); compared without "[]" and by whole segments;
// - the key "list.field" of a filter on `list` with a selector on `field` has
//   only the values that the filter lets through: "=" and "a|b" keep the values
//   of the filter, "!=" removes them; more selectors and filters on the same
//   list and field are in AND; values are compared as strings.
// Without an active projection with an Include nothing changes (view undefined).
// ---------------------------------------------------------------------------

// The key without selectors and "[]" markers; empty when it cannot be read
function plainKey(key) {
  if (typeof key !== "string") return "";

  const { segments } = parseFilterSegments(key.trim());
  return segments ? segments.map((segment) => segment.key).join(".") : "";
}

// The selectors of the valid filters, one by one: the list they act on, the
// field they test, the values and the filter they come from
function parseFilterSelectors(filters) {
  const selectors = [];

  for (const filter of Array.isArray(filters) ? filters : []) {
    if (validateFilter(filter)) continue;

    const { segments } = parseFilterSegments(filter);
    const list = segments.map(({ key }) => key).join(".");
    for (const { field, values, negate } of segments[segments.length - 1].selectors) {
      selectors.push({ list, field, values, negate, source: filter });
    }
  }

  return selectors;
}

// Does the filter selector let the value through? (the value as a string)
const lets = ({ values, negate }, value) => values.includes(String(value)) !== negate;

const views = new WeakMap();

/**
 * What the rules see of the document: the kept paths and the filters of an
 * active projection that has an Include. Cached by projection object.
 * @param {{active?: boolean, include?: string[], protect?: string[], filters?: string[]}} projection
 * @returns {{kept: string[], selectors: object[]}|undefined} Undefined when there is no projection to apply.
 */
export function projectionView(projection) {
  if (!projection || typeof projection !== "object") return undefined;
  if (views.has(projection)) return views.get(projection);

  const kept = projectionIncludes(projection);
  const view =
    kept.length > 0 ? { kept, selectors: parseFilterSelectors(projection.filters) } : undefined;

  views.set(projection, view);
  return view;
}

/**
 * Does the projection keep the key of a rule? (selectors and "[]" of the key are ignored)
 * @param {object|undefined} view - From projectionView.
 * @param {string} key - The key (a dotted path).
 * @returns {boolean} True without a view, and for a key that cannot be read.
 */
export function isKeyKept(view, key) {
  if (!view) return true;

  const path = plainKey(key);
  return path === "" || view.kept.some((kept) => coversRoot(kept, path));
}

/**
 * The keys that the projection removes.
 * @param {object|undefined} view - From projectionView.
 * @param {string[]} keys - The keys.
 * @returns {string[]}
 */
export function hiddenKeys(view, keys) {
  return view ? keys.filter((key) => !isKeyKept(view, key)) : [];
}

/**
 * The values of a key that a rule can find after the projection and its filters.
 * @param {object|undefined} view - From projectionView.
 * @param {string} key - The key (a dotted path).
 * @param {Array} values - The values of the key in the schema.
 * @returns {Array} The same array without a view or a filter on the key; none for a key that is removed.
 */
export function viewValues(view, key, values) {
  if (!view || !Array.isArray(values)) return values;
  if (!isKeyKept(view, key)) return [];

  const path = plainKey(key);
  const applicable = view.selectors.filter(({ list, field }) => `${list}.${field}` === path);

  return applicable.length === 0
    ? values
    : values.filter((value) => applicable.every((selector) => lets(selector, value)));
}

// The active rules of the pipeline that read a key: the transformer and the
// synthesizer rules, and the fingerprint ones when the fingerprint is on. (The
// conditions of tags and segments use the keys of the synthesizer rules, which are checked.)
export function activeRuleKeys(config) {
  const rules = (section, list) =>
    (Array.isArray(list) ? list : [])
      .filter((rule) => rule?.active && typeof rule.key === "string" && rule.key !== "")
      .map(({ key }) => ({ section, key }));

  return [
    ...rules("Transformer", config?.transformer?.rules),
    ...rules("Synthesizer", config?.synthesizer?.rules),
    ...(config?.fingerprinting?.active ? rules("Fingerprinting", config.fingerprinting.rules) : [])
  ];
}

/**
 * The active rules that read a field the projection removes: they would always
 * read nothing.
 * @param {object} config - The pipeline config (as in the editor).
 * @returns {{section: string, key: string}[]} Empty without an active projection with an Include.
 */
export function rulesOnRemovedFields(config) {
  const view = projectionView(config?.projection);
  return view ? activeRuleKeys(config).filter(({ key }) => !isKeyKept(view, key)) : [];
}

/**
 * The active rules whose "=" selector contradicts the filters of the projection
 * on the same list and field (eg. "titles[type=seo].text" with the filter
 * "titles[type=main]", or "[language.code=eng]" with "[language.code!=eng]"):
 * the filters remove every element the rule selects, so it is always empty. The
 * "!=" selectors of the rules are not checked.
 * @param {object} config - The pipeline config (as in the editor).
 * @returns {{section: string, key: string, field: string, values: string[], filters: string[]}[]}
 */
export function rulesContradictingFilters(config) {
  const view = projectionView(config?.projection);
  if (!view || view.selectors.length === 0) return [];

  const found = [];
  for (const { section, key } of activeRuleKeys(config)) {
    const { segments } = parseFilterSegments(key.trim());
    if (!segments) continue;

    const path = [];
    for (const { key: name, selectors } of segments) {
      path.push(name);
      const list = path.join(".");

      for (const selector of selectors) {
        if (selector.negate) continue;

        const applicable = view.selectors.filter(
          (filter) => filter.list === list && filter.field === selector.field
        );
        if (applicable.length === 0) continue;

        const survives = selector.values.some((value) =>
          applicable.every((filter) => lets(filter, value))
        );
        if (!survives) {
          found.push({
            section,
            key,
            field: selector.field,
            values: selector.values,
            filters: [...new Set(applicable.map(({ source }) => source))]
          });
        }
      }
    }
  }

  return found;
}

export const ruleContradictionMessage = ({ section, key, field, values, filters }) =>
  `${section}: the rule "${key}" selects ${field}=${values.join("|")}, but the filter${
    filters.length === 1 ? "" : "s"
  } ${filters.map((filter) => `"${filter}"`).join(", ")} remove${
    filters.length === 1 ? "s" : ""
  } those elements, so the rule will always be empty`;

// ---------------------------------------------------------------------------
// Picking the Include paths from the leaves of a JSON Schema
//
// The panel works only on the leaf paths of the schema (dotted, without "[]"
// markers, as the import of a JSON Schema gives them). The selection is a Set
// of those paths; "titles[].text" and "titles.text" are the same path.
// ---------------------------------------------------------------------------

/**
 * Groups the paths by their first key (what comes before the first "."), in
 * the order they first appear.
 * @param {string[]} paths - The leaf paths.
 * @returns {{key: string, paths: string[]}[]}
 */
export function groupPaths(paths) {
  const groups = new Map();

  for (const path of paths) {
    const key = normalizePath(path).split(".")[0];
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(path);
  }

  return [...groups].map(([key, list]) => ({ key, paths: list }));
}

/**
 * The paths that contain the text (case insensitive); all of them without text.
 * @param {string[]} paths - The leaf paths.
 * @param {string} query - The text to look for.
 * @returns {string[]}
 */
export function filterPaths(paths, query) {
  const text = String(query ?? "").trim().toLowerCase();
  return text ? paths.filter((path) => path.toLowerCase().includes(text)) : paths;
}

/**
 * The paths of the schema that are already in the Include, and so start ticked
 * (equivalent spellings match: "titles[].text" is "titles.text").
 * @param {string[]} include - The current Include.
 * @param {string[]} paths - The leaf paths of the schema.
 * @returns {Set<string>} The ticked paths, without "[]".
 */
export function initialSelection(include, paths) {
  const included = new Set((Array.isArray(include) ? include : []).map(normalizePath));
  return new Set(paths.map(normalizePath).filter((path) => included.has(path)));
}

/**
 * The Include paths that the schema does not contain (they cannot be ticked
 * or unticked, and are kept as they are).
 * @param {string[]} include - The current Include.
 * @param {string[]} paths - The leaf paths of the schema.
 * @returns {string[]}
 */
export function pathsOutsideSchema(include, paths) {
  const schema = new Set(paths.map(normalizePath));
  return (Array.isArray(include) ? include : []).filter(
    (path) => !schema.has(normalizePath(path))
  );
}

/**
 * Ticks or unticks paths, without changing the selection given.
 * @param {Set<string>} selected - The ticked paths.
 * @param {string[]} paths - The paths to tick or untick.
 * @param {boolean} checked - Tick (true) or untick (false).
 * @returns {Set<string>} The new selection.
 */
export function toggleSelection(selected, paths, checked) {
  const next = new Set(selected);
  paths.forEach((path) =>
    checked ? next.add(normalizePath(path)) : next.delete(normalizePath(path))
  );
  return next;
}

/**
 * The state of the checkbox of a group of paths.
 * @param {string[]} paths - The paths of the group.
 * @param {Set<string>} selected - The ticked paths.
 * @returns {"all"|"some"|"none"} "none" also for a group without paths.
 */
export function selectionState(paths, selected) {
  const ticked = paths.filter((path) => selected.has(normalizePath(path))).length;
  if (ticked === 0) return "none";
  return ticked === paths.length ? "all" : "some";
}

/**
 * The new Include once the selection is applied: the paths of the Include that
 * the schema does not contain stay as they are, the ones it contains stay only
 * if still ticked (with their own spelling), and the ticked ones that were not
 * there are added (the new ones first, in the order of the schema).
 * @param {string[]} include - The current Include.
 * @param {string[]} paths - The leaf paths of the schema.
 * @param {Set<string>} selected - The ticked paths.
 * @returns {{list: string[], added: number, removed: number}}
 */
export function applySelection(include, paths, selected) {
  const current = Array.isArray(include) ? include : [];
  const schema = new Set(paths.map(normalizePath));

  const kept = current.filter((path) => {
    const key = normalizePath(path);
    return !schema.has(key) || selected.has(key);
  });
  const ticked = paths.filter((path) => selected.has(normalizePath(path)));
  const { list, added } = mergePaths(kept, ticked);

  return { list, added, removed: current.length - kept.length };
}

// ---------------------------------------------------------------------------
// Coverage of schedule fields that Egress Keep Only Synthesizer Keys may drop
//
// Loader Input Projection dependencies are checked in the Loader editor. This
// section retains the separate Egress check for the synthesizer-keys option,
// which can remove schedule roots after their input projection.
// All the paths below are compared without the "[]" markers.
// ---------------------------------------------------------------------------

// An include P covers the root T of the windows when P is T, when P is an
// ancestor of T (the whole subtree is included), or when T is an ancestor of P
// (the root exists with selected fields inside).
const coversRoot = (include, root) =>
  include === root ||
  root.startsWith(`${include}.`) ||
  include.startsWith(`${root}.`);

/**
 * The paths kept by a projection (include and protect, which is always copied),
 * without the "[]" markers.
 * @param {{active?: boolean, include?: string[], protect?: string[]}} projection - The Loader Input Projection.
 * @returns {string[]} Empty when the projection is not active or has no include.
 */
export function projectionIncludes(projection) {
  if (!projection?.active || !Array.isArray(projection.include)) return [];
  if (projection.include.length === 0) return [];
  const protect = Array.isArray(projection.protect) ? projection.protect : [];
  return [...projection.include, ...protect].map(normalizePath).filter(Boolean);
}

const nameOf = (item, id) => item?.title || id;

// A "Patch Only" loader has neither pipelines nor schedules in the editor
const activeSchedules = (loader) =>
  loader?.patcher
    ? []
    : (loader?.scheduling?.schedules ?? []).filter((schedule) => schedule?.active);

const hasEgress = (pipeline) => Boolean(pipeline?.egress?.active);

const unique = (ids) => [...new Set(ids.filter(Boolean))];

/**
 * The pipelines that produce the egress the loader indexes, in the order and
 * with the rules of ares-content-loader and ares-pipeline.
 * - the loader needs injects.egress.active and its pipelines active, otherwise
 *   the document is not replaced by any egress: nothing counts;
 * - only the pipelines with egress.active have an egress;
 * - the FIRST egress wins, the static pipelines go before the dynamic one: if a
 *   static pipeline has the egress only the first one counts;
 * - otherwise every dynamic pipeline with the egress counts, because only one of
 *   them runs and which one depends on the document.
 * A pipeline that is not in the data is skipped (the backend skips it too).
 * @param {object} loader - The loader config.
 * @param {function(string): object|undefined} getPipeline - The config of a pipeline by id.
 * @returns {{id: string, pipeline: object}[]}
 */
export function egressPipelines(loader, getPipeline) {
  if (!loader?.injects?.egress?.active) return [];
  if (!loader.pipelines?.active || loader.patcher) return [];
  // From Catalog: the pipelines run on catalog documents, the egress does not apply
  if (loader.pipelines.fromCatalog?.active) return [];

  for (const id of unique(loader.pipelines.static ?? [])) {
    const pipeline = getPipeline(id);
    if (hasEgress(pipeline)) return [{ id, pipeline }];
  }

  return unique((loader.pipelines.dynamic ?? []).map((pipe) => pipe?.id))
    .map((id) => ({ id, pipeline: getPipeline(id) }))
    .filter(({ pipeline }) => hasEgress(pipeline));
}

// The first key of a path of the synthesizer, as ares-pipeline takes it for
// keepOnlySynthesizerKeys: what comes before the first "." or "[" (selectors
// and nested keys excluded)
const firstKey = (path) => String(path).split(/[.[]/, 1)[0];

// The keys that the egress keeps with keepOnlySynthesizerKeys: the first keys
// of the paths of the ACTIVE rules of the synthesizer
const synthesizerFirstKeys = (pipeline) =>
  new Set(
    (pipeline?.synthesizer?.rules ?? [])
      .filter((rule) => rule?.active)
      .map((rule) => firstKey(rule.key ?? ""))
      .filter(Boolean)
  );

// The findings of one pipeline that produces the egress of a loader. The
// Loader Projection runs before this pipeline, so schedule coverage belongs to
// the Loader's dependency checks. This helper retains only the Egress behavior
// that can remove fields from the produced document.
function gapsOf({ schedules, loader, pipelineId, pipeline }) {
  const name = nameOf(pipeline, pipelineId);
  const kept = pipeline?.egress?.keepOnlySynthesizerKeys
    ? synthesizerFirstKeys(pipeline)
    : undefined;
  const gaps = [];

  for (const schedule of schedules) {
    const base = { scheduleId: schedule.id, loader, pipelineId, pipeline: name };

    const rootField =
      typeof schedule.keys?.root === "string" ? schedule.keys.root.trim() : "";
    const key = normalizePath(rootField).split(".")[0];
    if (kept && key && !kept.has(key)) {
      gaps.push({ ...base, field: rootField, reason: "synthKeys", key });
    }
  }

  return gaps;
}

// The loaders, with their active schedules, where the pipeline is one of those
// that produce the egress the loader indexes. The pipeline given (the one in
// the form) replaces the saved copy; the other ones are read with getPipeline.
function loadersUsing(pipelineId, pipeline, loaders, getPipeline) {
  const lookup = (id) => (id === pipelineId ? pipeline : getPipeline?.(id));
  const found = [];

  for (const loader of loaders ?? []) {
    const schedules = activeSchedules(loader);
    if (schedules.length === 0) continue;

    const counts = egressPipelines(loader, lookup).some(({ id }) => id === pipelineId);
    if (counts) found.push({ loader, schedules });
  }

  return found;
}

/**
 * Loader side: the fields used by the active schedules of the loader that a
 * pipeline producing its egress does not keep.
 * @param {object} loader - The loader config (as in the editor).
 * @param {function(string): object|undefined} getPipeline - The config of a pipeline by id.
 * @param {string} [loaderName] - The name shown in the messages (default: the title).
 * @returns {{field: string, reason: string, key?: string, scheduleId: string, loader: string, pipelineId: string, pipeline: string}[]}
 */
export function loaderCoverageGaps(loader, getPipeline, loaderName) {
  const schedules = activeSchedules(loader);
  if (schedules.length === 0) return [];

  const name = loaderName || nameOf(loader, loader?.id);

  return egressPipelines(loader, getPipeline).flatMap(({ id, pipeline }) =>
    gapsOf({ schedules, loader: name, pipelineId: id, pipeline })
  );
}

/**
 * Pipeline side: the fields used by the active schedules of the loaders that
 * run the pipeline, that the pipeline does not keep. The pipeline is checked
 * only for the loaders where it is one of those that produce the egress (the
 * other pipelines of the loader are read with `getPipeline`; for this one the
 * config given is used).
 * @param {string} pipelineId - The id of the pipeline.
 * @param {object} pipeline - The pipeline config (as in the editor).
 * @param {Iterable<object>} loaders - The loaders to look into.
 * @param {function(string): object|undefined} getPipeline - The config of the other pipelines by id.
 * @returns {{field: string, reason: string, key?: string, scheduleId: string, loader: string, pipelineId: string, pipeline: string}[]}
 */
export function pipelineCoverageGaps(pipelineId, pipeline, loaders, getPipeline) {
  // Nothing to check without egress or when Keep Only Synthesizer Keys is off.
  if (!pipelineId || !hasEgress(pipeline)) return [];
  if (!pipeline.egress.keepOnlySynthesizerKeys) return [];

  return loadersUsing(pipelineId, pipeline, loaders, getPipeline).flatMap(
    ({ loader, schedules }) =>
      gapsOf({
        schedules,
        loader: nameOf(loader, loader.id),
        pipelineId,
        pipeline
      })
  );
}

/**
 * The fields that the active schedules of the loaders need from a pipeline: the
 * root, and start and end under it (the paths without "[]"). Only the loaders
 * where the pipeline produces the egress that is indexed are looked into, as in
 * pipelineCoverageGaps.
 * @param {string} pipelineId - The id of the pipeline.
 * @param {object} pipeline - The pipeline config (as in the editor).
 * @param {Iterable<object>} loaders - The loaders to look into.
 * @param {function(string): object|undefined} getPipeline - The config of the other pipelines by id.
 * @returns {Map<string, {scheduleId: string, loader: string, key: "root"|"start"|"end"}[]>}
 */
export function scheduleRequirements(pipelineId, pipeline, loaders, getPipeline) {
  const required = new Map();
  if (!pipelineId || !hasEgress(pipeline)) return required;

  const add = (path, entry) => {
    if (!required.has(path)) required.set(path, []);
    required.get(path).push(entry);
  };

  for (const { loader, schedules } of loadersUsing(pipelineId, pipeline, loaders, getPipeline)) {
    const name = nameOf(loader, loader.id);

    for (const schedule of schedules) {
      const keys = schedule.keys ?? {};
      const root = normalizePath(keys.root);
      if (!root) continue;

      const base = { scheduleId: schedule.id, loader: name };
      add(root, { ...base, key: "root" });
      ["start", "end"].forEach((key) => {
        const field = normalizePath(keys[key]);
        if (field) add(`${root}.${field}`, { ...base, key });
      });
    }
  }

  return required;
}

export const coverageMessage = ({
  field,
  scheduleId,
  loader,
  pipeline,
  reason,
  key
}) =>
  reason === "synthKeys"
    ? `Il campo ${field} è usato dallo schedule ${scheduleId} del loader ${loader} ma la pipeline ${pipeline} tiene solo le chiavi del synthesizer (Keep Only Synthesizer Keys) e ${key} non è tra queste`
    : `Il campo ${field} è usato dallo schedule ${scheduleId} del loader ${loader} ma la pipeline ${pipeline} non lo include`;

/**
 * The messages of a list of gaps in a single text, limited to `max` messages.
 * @param {object[]} gaps - The result of loaderCoverageGaps or pipelineCoverageGaps.
 * @param {number} [max=5] - The maximum number of messages.
 * @returns {string}
 */
export function coverageText(gaps, max = 5) {
  const messages = gaps.slice(0, max).map(coverageMessage);
  if (gaps.length > max) messages.push(`... e altri ${gaps.length - max}`);

  return messages.join(" - ");
}
